import { useCallback, useState } from 'react';
import Grid from '@mui/material/Unstable_Grid2';
import ToggleButton from '@mui/material/ToggleButton';
import ExtensionIcon from '@mui/icons-material/Extension';
import { TableColumn } from '@backstage/core-components';
import {
  ApiEntity,
  Entity,
  RELATION_OWNED_BY,
  RELATION_PART_OF,
} from '@backstage/catalog-model';
import { EntitySwitch } from '@backstage/plugin-catalog';
import { EntityTable } from '@backstage/plugin-catalog-react';
import {
  Direction,
  EntityCatalogGraphCard,
} from '@backstage/plugin-catalog-graph';
import {
  ApiDefinitionDialog,
  ApiTypeTitle,
  EntityConsumedApisCard,
  EntityProvidedApisCard,
} from '@backstage/plugin-api-docs';
import {
  EntityAdrContent,
  isAdrAvailable,
} from '@backstage-community/plugin-adr';
import {
  EntityTechdocsContent,
  isTechDocsAvailable,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import {
  ReportIssue,
  LightBox,
} from '@backstage/plugin-techdocs-module-addons-contrib';
import { Mermaid } from 'backstage-plugin-techdocs-addon-mermaid';
import {
  EntityGrafanaAlertsCard,
  EntityGrafanaDashboardsCard,
  isAlertSelectorAvailable,
  isDashboardSelectorAvailable,
} from '@backstage-community/plugin-grafana';
import {
  isPluginApplicableToEntity as isPagerDutyAvailable,
  EntityPagerDutyCard,
} from '@pagerduty/backstage-plugin';
import { EntityLayout } from '@internal/plugin-medidata-catalog-react';
import { EntityPackagesContent } from '@internal/plugin-packages';
import {
  CICDContent,
  commonContent,
  maturityScoreContent,
} from './commonContent';

const ApiDefinitionButton = ({
  apiEntity,
}: Readonly<{ apiEntity: ApiEntity }>) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const handleClick = useCallback(() => setDialogOpen(isOpen => !isOpen), []);
  const handleClose = useCallback(() => setDialogOpen(false), []);

  return (
    <>
      <ToggleButton
        aria-label="Toggle API Definition Dialog"
        onClick={handleClick}
        value={dialogOpen}
      >
        <ExtensionIcon />
      </ToggleButton>
      <ApiDefinitionDialog
        entity={apiEntity}
        open={dialogOpen}
        onClose={handleClose}
      />
    </>
  );
};

const apiEntityColumns: TableColumn<ApiEntity>[] = [
  EntityTable.columns.createEntityRefColumn({ defaultKind: 'API' }),
  EntityTable.columns.createEntityRelationColumn({
    title: 'Product',
    relation: RELATION_PART_OF,
    defaultKind: 'system',
    filter: {
      kind: 'system',
    },
  }),
  EntityTable.columns.createEntityRelationColumn({
    title: 'Team(s)',
    relation: RELATION_OWNED_BY,
    defaultKind: 'group',
  }),
  {
    title: 'Type',
    field: 'spec.type',
    render: entity => <ApiTypeTitle apiEntity={entity} />,
  },
  EntityTable.columns.createSpecLifecycleColumn(),
  EntityTable.columns.createMetadataDescriptionColumn(),
  {
    title: 'API Definition',
    render: entity => <ApiDefinitionButton apiEntity={entity} />,
  },
];

export const serviceEntityPage = (
  <EntityLayout parentEntityRelations={[RELATION_PART_OF]}>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={1} alignItems="stretch">
        {commonContent}
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/maturity" title="Maturity">
      {maturityScoreContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/observability" title="Observability">
      <Grid container spacing={1}>
        <EntitySwitch>
          <EntitySwitch.Case if={isPagerDutyAvailable}>
            <Grid md={7}>
              <EntityPagerDutyCard readOnly />
            </Grid>
          </EntitySwitch.Case>
        </EntitySwitch>

        <Grid container md={5} spacing={1} alignContent="start">
          <EntitySwitch>
            <EntitySwitch.Case
              if={(entity: Entity) =>
                Boolean(isDashboardSelectorAvailable(entity))
              }
            >
              <Grid md={12}>
                <EntityGrafanaDashboardsCard
                  title="Vision Dashboards"
                  paged
                  pageSize={10}
                  sortable
                />
              </Grid>
            </EntitySwitch.Case>
          </EntitySwitch>

          <EntitySwitch>
            <EntitySwitch.Case if={isAlertSelectorAvailable}>
              <Grid md={12}>
                <EntityGrafanaAlertsCard
                  title="Vision Alerts"
                  paged
                  pageSize={10}
                  sortable
                  showState
                />
              </Grid>
            </EntitySwitch.Case>
          </EntitySwitch>
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/diagram" title="Diagram">
      <EntityCatalogGraphCard
        direction={Direction.LEFT_RIGHT}
        title="Service Diagram"
        height={700}
        zoom="enabled"
        unidirectional={false}
      />
    </EntityLayout.Route>

    <EntityLayout.Route if={isAdrAvailable} path="/adrs" title="ADRs">
      <EntityAdrContent />
    </EntityLayout.Route>

    <EntityLayout.Route path="/api" title="API">
      <Grid container spacing={1} alignItems="stretch">
        <Grid md={12}>
          <EntityProvidedApisCard columns={apiEntityColumns} />
        </Grid>
        <Grid md={12}>
          <EntityConsumedApisCard columns={apiEntityColumns} />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs" if={isTechDocsAvailable}>
      <EntityTechdocsContent>
        <TechDocsAddons>
          <Mermaid
            config={{
              theme: 'forest',
              themeVariables: { lineColor: '#000000' },
            }}
          />
          <LightBox />
          <ReportIssue />
        </TechDocsAddons>
      </EntityTechdocsContent>
    </EntityLayout.Route>

    <EntityLayout.Route path="/ci-cd" title="CI/CD">
      {CICDContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/packages" title="Packages">
      <EntityPackagesContent />
    </EntityLayout.Route>
  </EntityLayout>
);
